// Imports ------------------------------------------------------------------->

import {Service, service, inject} from "./di";
import {StorageService} from "./StorageService";
import {RuntimeConfig} from "./ConfigService";
import {Flyer} from "../definitions/FlyerDefinitions";
import {StringEnum} from "../definitions/CommonDefinitions";
import {AggregateSourceAction} from "./definitions/IAnalyticsService";

// Private Definitions ------------------------------------------------------->

type SentAnalytics = {
	[eventType: string]: {
		[flyerId: string]: number;
	}
};

/* Need a way to separate between analytics send by the default experience vs. the
	* distribution experience. Save each profile into different keys
	*/
class AnalyticsStorageKey extends StringEnum {
	static DEFAULT = new AnalyticsStorageKey("sent_analytics");
	static DISTRIBUTION = new AnalyticsStorageKey("dist_analytics");
}

// Public Definitions -------------------------------------------------------->

export type Engagement = "open" | "ev";
export type EngagementStatus = "billable" | "repeat" | "non-premium";

@service("evTrackingService")
export class EVTrackingService extends Service {
	@inject storage: StorageService;

	private _sentAnalytics: SentAnalytics;
	private _storageKey = AnalyticsStorageKey.DEFAULT;
	private _lastVisitedFlyer: Flyer;
	private _lastVisitedFlyerSourceAction: AggregateSourceAction;

	init(val: RuntimeConfig) {
		if (this._sentAnalytics) {
			throw new Error("Trying to call init() more than once on EVTrackingService. This shouldn't be done");
		}

		if (val === RuntimeConfig.DISTRIBUTION) {
			this._storageKey = AnalyticsStorageKey.DISTRIBUTION;
		}

		this._initSentAnalytics();
		this._pruneSentAnalytics();
	}

	isFreshFlyer(flyerId: number) {
		return !(flyerId.toString() in this._sentAnalytics["open"]);
	}

	hasEngaged(flyerId: number, type: Engagement) {
		return this._sentAnalytics[type][flyerId];
	}

	setEngagement(flyerId: number, type: Engagement) {
		this._sentAnalytics[type][`${flyerId}`] = Date.now();
		this._saveSentAnalytics();
	}

	engagementStatus(flyer: Flyer): EngagementStatus {
		const repeat = flyer.id.toString() in this._sentAnalytics["ev"];
		const billable = flyer.premium && !repeat;
		return billable ? "billable" :
					repeat ? "repeat" :
					"non-premium";
	}


	getlastVisitedFlyer(): Flyer {
		return this._lastVisitedFlyer;
	}

	savelastVisitedFlyer(value: Flyer) {
		this._lastVisitedFlyer = value;
	}


	getlastVisitedFlyerSourceAction(): AggregateSourceAction {
		return this._lastVisitedFlyerSourceAction;
	}

	savelastVisitedFlyerSourceAction(value: AggregateSourceAction) {
		this._lastVisitedFlyerSourceAction = value;
	}

	private _saveSentAnalytics() {
		this.storage.setItem(this._storageKey.toString(),
												JSON.stringify(this._sentAnalytics));
	}

	private _initSentAnalytics(): void {
		const sentAnalyticsString = this.storage.getItem(this._storageKey.toString());
		if (sentAnalyticsString) {
			this._sentAnalytics = JSON.parse(sentAnalyticsString);
		} else {
			this._sentAnalytics = {
				open: {},
				ev: {}
			};
		}
	}

	private _pruneSentAnalytics(): void {
		const now = Date.now();
		for (const ev in this._sentAnalytics) {
			if (!this._sentAnalytics.hasOwnProperty(ev))
				continue;

			const ids = this._sentAnalytics[ev];
			const dead: string[] = [];
			for (const id in ids)
				if (now - ids[id] > 1000 * 60 * 60 * 24 * 7)
					dead.push(id);
			for (const id of dead)
				delete ids[id];
		}
		this._saveSentAnalytics();
	}
}
